import * as React from 'react';
import { StatusType } from 'types/status';
import { buildClassString } from '@ps/payscale-shared-ui/lib/StringUtils';
import './alert.scss';

interface AlertProps {
    className?: string;                               // className to add to top-level elment
    children?: React.ReactNode;                       // children to render inside alert box
    onClose?: () => void;                             // callback for when user clicks on close button
    showCloseButton?: boolean;                        // whether or not to show close button/allow user to close
    title?: string;                                   // title text to display at top of alert
    type: StatusType;                                 // type of alert (Warning, Info, etc)
}

class Alert extends React.PureComponent<AlertProps, {}> {
    render(): React.ReactNode {
        const {
            className,
            children,
            onClose,
            showCloseButton,
            title,
            type } = this.props;

        let classString = buildClassString({
            alert: true,
            info: type === StatusType.Info,
            warning: type === StatusType.Warning,
            error: type === StatusType.Error,
            success: type === StatusType.Success
        });
        if (className) {
            classString += ` ${className}`;
        }

        return (
            <div className={classString}>
                {this._renderIcon(type)}
                <div className="alert__content">
                    {this._renderTitle(title)}
                    {children}
                </div>
                {this._renderCloseButton(!!showCloseButton, onClose)}
            </div>
        );
    }

    // Render alert icon based on type of alert
    private _renderIcon(type: StatusType): React.ReactNode {
        let icon: JSX.Element;
        if (type === StatusType.Error) {
            icon = (
                <div className="icon-reverse icon-circle">
                    <i className="icon-attention-alt" aria-hidden="true" />
                </div>
            );
        } else if (type === StatusType.Warning) {
            icon = (
                <i className="icon-warning" aria-hidden="true" />
            );
        } else if (type === StatusType.Success) {
            icon = (
                <div className="icon-reverse icon-circle">
                    <i className="icon-check" aria-hidden="true" />
                </div>
            );
        } else {
            icon = (
                <i className="icon-lightbulb" aria-hidden="true" />
            );
        }

        return (
            <div className="alert__icon">
                {icon}
            </div>
        );
    }

    // Render alert title, if present
    private _renderTitle(title?: string): React.ReactNode {
        if (!title) {
            return null;
        }

        return (
            <div className="alert__title">{title}</div>
        );
    }

    // Render close button at top-right of alert if props indicate we should
    private _renderCloseButton(showCloseButton: boolean, onClose?: () => void): React.ReactNode {
        if (!showCloseButton) {
            return null;
        }

        return (
            <button type="button" className="alert__close-button" onClick={onClose}>
                <i className="icon-cancel" />
            </button>
        );
    }
}

export { Alert };
export default Alert;



// WEBPACK FOOTER //
// ./src/components/common/alert.tsx